import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendUp } from '@phosphor-icons/react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Discreet social-proof counter for the HomePage hero.
 * "[N] cars washed this week — was yours one of them?"
 *
 * Animates count-up on mount; live-subscribes to /api/stats/live (SSE).
 * Each `wash` SSE event spawns a "+1" sprite that floats up and fades away.
 */
export default function WeeklyWashCounter() {
  const { t } = useLanguage();
  const [count, setCount] = useState(null);
  const [display, setDisplay] = useState(0);
  const [bursts, setBursts] = useState([]); // each: { id, delta }
  const rafRef = useRef(null);

  // SSE subscription (with REST fallback)
  useEffect(() => {
    let es;
    let pollId;
    let cancelled = false;

    const restFallback = () => {
      const tick = () => {
        axios.get(`${API}/stats/public-counter`).then((r) => {
          if (!cancelled) setCount(r.data?.weekly_washes ?? 0);
        }).catch(() => {});
      };
      tick();
      pollId = setInterval(tick, 60 * 1000);
    };

    try {
      es = new EventSource(`${API}/stats/live`);
      es.addEventListener('init', (e) => {
        try { setCount(JSON.parse(e.data).count); } catch (err) { console.warn('[LiveCounter] bad init payload', err?.message); }
      });
      es.addEventListener('ping', (e) => {
        try { setCount(JSON.parse(e.data).count); } catch (err) { console.warn('[LiveCounter] bad ping payload', err?.message); }
      });
      es.addEventListener('wash', (e) => {
        try {
          const data = JSON.parse(e.data);
          setCount(data.count);
          const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          setBursts((b) => [...b, { id, delta: data.delta || 1 }]);
          // auto-remove after animation
          setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1500);
        } catch (err) { console.warn('[LiveCounter] bad wash payload', err?.message); }
      });
      es.onerror = () => {
        es.close();
        if (!cancelled) restFallback();
      };
    } catch (_) {
      restFallback();
    }

    return () => {
      cancelled = true;
      if (es) es.close();
      if (pollId) clearInterval(pollId);
    };
  }, []);

  // Smooth count-up animation when count changes
  useEffect(() => {
    if (count == null) return;
    const start = display;
    const target = count;
    if (start === target) return;
    const duration = 1100;
    const startedAt = performance.now();
    const step = (now) => {
      const t1 = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - t1, 3);
      setDisplay(Math.round(start + (target - start) * eased));
      if (t1 < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  if (count == null) return null;

  const phrase = t('counterTemplate').replace('{n}', display.toLocaleString());

  return (
    <motion.div
      className="flex items-center gap-2 text-[#FF5A00]/90 mt-3 text-xs relative"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.25 }}
      data-testid="weekly-wash-counter"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A00] animate-pulse" />
      <TrendUp size={12} weight="bold" />
      <span className="leading-tight">{phrase}</span>

      {/* Fly-away "+N" sprites for each live wash event */}
      <AnimatePresence>
        {bursts.map((b) => (
          <motion.span
            key={b.id}
            initial={{ opacity: 0, y: 0, scale: 0.7 }}
            animate={{ opacity: [0, 1, 1, 0], y: -36, scale: [0.7, 1.1, 1.0, 0.9] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: 'easeOut' }}
            className="absolute right-0 -top-1 text-[11px] font-bold text-[#FF5A00] drop-shadow-[0_0_10px_rgba(255,90,0,0.7)] pointer-events-none"
            data-testid="counter-burst"
          >
            +{b.delta}
          </motion.span>
        ))}
      </AnimatePresence>
    </motion.div>
  );
}
